import { recordArtifactViewed } from '@streem/analytics';
import { DetailSession, Recording } from '@streem/sdk-core';
import { Column, Skeleton, styled } from '@streem/ui-react';
import { CSSProperties } from 'react';
import { WallItem } from 'streem-sdk-protobuf';
import { PreviewButton } from './preview_button';
import {
    MAX_VIDEO_HEIGHT,
    MAX_VIDEO_SECTION_HEIGHT,
    MAX_VIDEO_WIDTH,
    MediaGalleryHeader,
} from './shared.styles';
import { RecordingThumbnail, RecordingTrackThumbnail } from './thumbnail';
import { UnsupportedBrowserInfoCard } from './unsupported_browser_info_card';
import { VideoPreviewInfoCard } from './video_preview_info_card';
import { useCallRecording } from '../../hooks/use_call_recording';

interface CallRecordingProps {
    recordings: Recording[];
    previewEnabled?: boolean;
    selectionVisible: boolean;
    handleArtifactSelect: (artifactType: WallItem.ArtifactType, artifactSid: string) => void;
    detailSession: DetailSession;
    selectedRecordingIds: Set<string>;
}
export function CallRecording({
    recordings,
    previewEnabled,
    selectionVisible,
    handleArtifactSelect,
    detailSession,
    selectedRecordingIds,
}: CallRecordingProps): JSX.Element {
    const {
        isAdmin,
        somePreviewsReady,
        showVideoPreviewInfoCard,
        setShowVideoPreviewInfoCard,
        videoPreviewTermsAccepted,
        setVideoPreviewTermsAccepted,
        browserCanPlayPreview,
        someRecordingsPending,
        someRecordingsReady,
        showVideoPreview,
        timeUpdateHandler,
    } = useCallRecording(recordings, previewEnabled);

    return (
        <CallRecordingContainer>
            <MediaGalleryHeader>Call Recording</MediaGalleryHeader>
            {someRecordingsPending && !previewEnabled && (
                // If the company setting for video preview is FALSE and the recording is pending we only want a skeleton.
                <Skeleton styleOverrides={skeletonStyles}></Skeleton>
            )}
            {someRecordingsPending && previewEnabled && !showVideoPreview && (
                // If the company setting for video preview is TRUE, the recording is pending, and the preview is NOT ready we should only see the skeleton.
                // Once the preview is ready, then we should see a skeleton and a preview button.
                // Once we click the preview button, we should see the skeleton and the video preview info card.
                // Once we accept terms, the skeleton and info card should disappear and we should see the video preview.
                <>
                    <Skeleton>
                        {somePreviewsReady &&
                            browserCanPlayPreview &&
                            !showVideoPreviewInfoCard && (
                                <PreviewButton onClick={() => setShowVideoPreviewInfoCard(true)} />
                            )}
                        {somePreviewsReady && !browserCanPlayPreview && (
                            <UnsupportedBrowserInfoCard />
                        )}
                        {somePreviewsReady &&
                            showVideoPreviewInfoCard &&
                            !videoPreviewTermsAccepted && (
                                <VideoPreviewInfoCard
                                    handleOkay={() => setVideoPreviewTermsAccepted(true)}
                                    handleCancel={() => setShowVideoPreviewInfoCard(false)}
                                />
                            )}
                    </Skeleton>
                </>
            )}
            {showVideoPreview &&
                recordings.map(recording => (
                    <RecordingTrackThumbnail
                        canSelect={false}
                        isSelected={false}
                        isSelectVisible={selectionVisible}
                        key={`preview-${recording.id}`}
                        artifact={recording}
                        onPlay={() =>
                            recordArtifactViewed({
                                artifactSid: recording.id,
                                roomSid: detailSession.roomId.toString(),
                                recordingTrack: {},
                            })
                        }
                    />
                ))}
            {someRecordingsReady &&
                recordings.map(recording => {
                    return (
                        <RecordingThumbnail
                            canSelect={isAdmin}
                            isSelected={selectedRecordingIds.has(recording.id)}
                            isSelectVisible={selectionVisible}
                            key={recording.id}
                            onSelect={() =>
                                handleArtifactSelect(WallItem.ArtifactType.RECORDING, recording.id)
                            }
                            artifact={recording}
                            onPlay={() =>
                                recordArtifactViewed({
                                    artifactSid: recording.id,
                                    roomSid: detailSession.roomId.toString(),
                                    recording: {},
                                })
                            }
                            timeUpdateHandler={timeUpdateHandler}
                            videoStyleOverrides={{
                                maxWidth: MAX_VIDEO_WIDTH,
                                maxHeight: MAX_VIDEO_HEIGHT,
                            }}
                        />
                    );
                })}
        </CallRecordingContainer>
    );
}

// these approximate the size of the video
const skeletonStyles: CSSProperties = {
    minWidth: '15.75rem',
    minHeight: '8.8125rem',
    height: MAX_VIDEO_HEIGHT,
    // default size is 44.5rem wide by 25rem tall, but can shrink down to 15.75rem wide by 8.8rem tall
    flex: `0 1 ${MAX_VIDEO_WIDTH}`,
};

const CallRecordingContainer = styled(Column)({
    maxWidth: MAX_VIDEO_WIDTH,
    maxHeight: MAX_VIDEO_SECTION_HEIGHT,
});
